import { apiClient } from './apiClient';
import { clearSchoolLogoCache } from '../hooks/useSchoolLogo';

/**
 * Single entry point for "who is the current user".
 *
 * The email/slug that ACV passes (URL params on iframe load, or the postMessage
 * user payload) is the source of truth. Whatever is cached in localStorage from a
 * previous session in the same browser must never win over it — otherwise uploads
 * get stamped with the wrong owner and notifications go to the wrong person.
 */

export const IDENTITY_CHANGED_EVENT = 'airecording-identity-changed';

export interface IdentityChangedDetail {
  email: string;
  slug: string | null;
}

/** Keys that belong to a specific signed-in user and must not leak to the next one. */
const USER_SCOPED_KEYS = ['user', 'userEmail', 'slug', 'msg', 'access_token', 'user-role'];

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export const normalizeSlug = (slug: string | null | undefined): string | null => {
  if (!slug) return null;
  const clean = String(slug).replace(/^\?slug=/, '').trim();
  return clean || null;
};

/**
 * Applies an identity coming from ACV. If it belongs to a different user than the
 * one cached in this browser, all user-scoped cache is cleared first.
 * Returns true when the user changed.
 */
export function applyIdentity(email: string | null | undefined, slug?: string | null): boolean {
  if (!email || !email.trim()) return false;

  const nextEmail = email.trim();
  const nextSlug = normalizeSlug(slug);
  const prevEmail = localStorage.getItem('userEmail');
  const prevSlug = localStorage.getItem('slug');
  const userChanged = !prevEmail || normalizeEmail(prevEmail) !== normalizeEmail(nextEmail);

  if (userChanged) {
    USER_SCOPED_KEYS.forEach((k) => localStorage.removeItem(k));
    clearSchoolLogoCache();
  }

  localStorage.setItem('userEmail', nextEmail);
  if (nextSlug) localStorage.setItem('slug', nextSlug);

  const effectiveSlug = nextSlug || localStorage.getItem('slug');
  apiClient.setUserContext(nextEmail, effectiveSlug || undefined);

  if (userChanged || (nextSlug && nextSlug !== prevSlug)) {
    window.dispatchEvent(
      new CustomEvent<IdentityChangedDetail>(IDENTITY_CHANGED_EVENT, {
        detail: { email: nextEmail, slug: effectiveSlug },
      }),
    );
  }

  return userChanged;
}

/**
 * Older share-page code stored a fake `anon-…@airecording.local` email as the viewer's
 * identity when they sent an emoji. Remove it so it can't be used for likes or comments.
 */
export function purgeLegacyAnonIdentity(): void {
  const stored = localStorage.getItem('userEmail');
  if (stored && stored.endsWith('@airecording.local')) {
    USER_SCOPED_KEYS.forEach((k) => localStorage.removeItem(k));
  }
}
